import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateRoom, useUpdateRoom } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { Room } from '@/types'

const roomFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  floor: z.string().max(50, 'Floor is too long').optional(),
})

type RoomFormValues = z.infer<typeof roomFormSchema>

interface RoomFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  buildingId: string
  room?: Room
}

export function RoomFormDialog({
  open,
  onOpenChange,
  buildingId,
  room,
}: RoomFormDialogProps) {
  const isEditing = !!room

  const createMutation = useCreateRoom(buildingId)
  const updateMutation = useUpdateRoom(room?.id ?? '', buildingId)

  const form = useForm<RoomFormValues>({
    resolver: zodResolver(roomFormSchema),
    defaultValues: {
      name: room?.name ?? '',
      floor: room?.floor ?? '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: room?.name ?? '',
        floor: room?.floor ?? '',
      })
    }
  }, [open, room, form])

  const isPending = createMutation.isPending || updateMutation.isPending

  function onSubmit(values: RoomFormValues) {
    const data = {
      name: values.name,
      floor: values.floor || undefined,
    }

    if (isEditing) {
      updateMutation.mutate(data, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    } else {
      createMutation.mutate(
        { ...data, building_id: buildingId },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Room' : 'Add Room'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the room details below.'
              : 'Enter the details for the new room.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Room name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="floor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Floor</FormLabel>
                  <FormControl>
                    <Input placeholder="Floor (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Add Room'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
